import { createIsomorphicFn } from '@tanstack/react-start';
import { getRequest, getRequestHeaders, setResponseHeader } from '@tanstack/react-start/server';
import { parse, serialize } from 'cookie-es';

import { defaultLocale } from './constants/locales';
import { getLocaleFromPath, isLocaleValid } from './utils';

/**
 * Локаль запроса — только серверная история (кука + путь + Accept-Language). Обёртка в
 * createIsomorphicFn обязательна: `src/router.tsx` теперь клиентская точка входа Start, и её
 * статический граф проверяется import-protection'ом — прямой импорт `@tanstack/react-start/server`
 * оттуда роняет сборку. На клиенте ветка вырезается вместе с серверными импортами.
 */
export const getLocaleFromRequest = createIsomorphicFn()
  .client(() => defaultLocale)
  .server(() => {
    const request = getRequest();
    const headers = getRequestHeaders();

    const url = new URL(request.url);

    // URL path locale takes highest priority — it's the most explicit user intent.
    // When someone visits /ru/privacy-policy, the URL says "Russian" regardless of cookie.
    const pathSegments = url.pathname.split('/').filter(Boolean);
    const firstSegment = pathSegments[0];

    if (firstSegment && isLocaleValid(firstSegment) && firstSegment !== defaultLocale) {
      const cookie = parse(headers.get('cookie') ?? '');

      if (cookie.locale !== firstSegment) {
        setResponseHeader(
          'Set-Cookie',
          serialize('locale', firstSegment, {
            maxAge: 30 * 24 * 60 * 60,
            path: '/',
          })
        );
      }

      return firstSegment;
    }

    const cookie = parse(headers.get('cookie') ?? '');

    if (cookie.locale && isLocaleValid(cookie.locale)) {
      return cookie.locale;
    }

    const queryLocale = getLocaleFromPath(url);

    if (isLocaleValid(queryLocale)) {
      setResponseHeader(
        'Set-Cookie',
        serialize('locale', queryLocale, {
          maxAge: 30 * 24 * 60 * 60,
          path: '/',
        })
      );

      return queryLocale;
    }

    // Mostly used for API requests
    const acceptLanguage = headers.get('accept-language');

    if (acceptLanguage && isLocaleValid(acceptLanguage)) {
      return acceptLanguage;
    }

    setResponseHeader(
      'Set-Cookie',
      serialize('locale', defaultLocale, {
        maxAge: 30 * 24 * 60 * 60,
        path: '/',
      })
    );

    return defaultLocale;
  });
